import Image from "next/image";
import { ChefHat, TrendingUp, type LucideIcon } from "lucide-react";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Floating notification cards over the device cluster — one story per device
 * (CRM like on the phone, sales insight on the laptop, kitchen ticket on the
 * tablet). Positions are % of the cluster wrapper like the devices, so the
 * cards travel with them across breakpoints. Each card slides in from its
 * nearest side (.jd-slide-in, direction via `from`), staggered by `pop`,
 * then keeps bobbing (.jd-float, desynced via `bob`).
 *
 * The `pop` values are spread roughly a beat apart — the cards land about one,
 * two and two and a half seconds in — so the scene keeps assembling for most
 * of the slide's 3s hold (SLIDE_MS in hero-carousel.tsx) rather than being
 * finished in the first second. The last one has to settle before the reel
 * moves on, so `pop` + the 0.7s .jd-slide-in has to stay inside SLIDE_MS.
 */
type NotifyCard = {
  /** Photo thumbnail (wins over icon when both are set) */
  img?: string;
  icon?: LucideIcon;
  iconClass?: string;
  /** Key under `home.mocks.notify` holding this card's title and sub. */
  key: string;
  /** Red LIVE pill after the title */
  live?: boolean;
  /** Placement inside the cluster wrapper, below sm. Unprefixed utilities. */
  pos: string;
  /** `sm:`-prefixed overrides restoring the comp's own arrangement, which is
      what every width from 640px up gets — the comp placed these cards the
      same way at every size, and only a phone-width cluster is too small to
      take it. Anything this leaves out keeps its `pos` value, so a card that
      sits in the same spot at every width omits it entirely. Written out in
      full rather than composed from `pos` at render time — Tailwind scans the
      source for literal class names, so a `sm:${...}` template would generate
      nothing. */
  posSm?: string;
  from: string;
  pop: string;
  bob: string;
  /** Stacking against the devices. Defaults to `z-[3]`, clear of all three;
      set it lower to tuck a card behind one (see the kaprao card below). */
  z?: string;
};

const NOTIFY_CARDS: NotifyCard[] = [
  {
    img: "/v2/kaprao.jpg",
    key: "ordering",
    live: true,
    // The one card that needs two placements, because it cannot shrink: its
    // title is the longest of the three and the row is `whitespace-nowrap`, so
    // at 375px it measures 274px against a 331px-wide cluster — laid over the
    // laptop it covers the whole machine rather than decorating it.
    //
    // Below sm it therefore sits clear above the laptop, in the empty upper
    // half of the stage cell. `bottom-full` rather than a negative `top`: the
    // offset has to stay a fixed distance from the laptop's top edge, and a
    // percentage is read against the wrapper's height, which grows with the
    // viewport — enough to lift the card clean out of the stage and into the
    // copy above it.
    pos: "bottom-full left-[6%]",
    // From sm the comp's own arrangement is back, unchanged: level with the
    // top of the phone, which lands at ~31% of the wrapper (78%-width laptop
    // sets the height; the phone is 19% wide and hangs 10% below the foot), so
    // 24% puts the card across it. `left-[6%]` carries over.
    //
    // sm, not lg: the comp draws this card at 24% at every size, and the only
    // width that cannot take it is a phone's. At 375px the card is 274px
    // against a 331px cluster (83%); by 640px it is 321px against 608px (53%),
    // the same relationship the full-width composition has. Splitting at lg
    // handed the phone treatment to every browser window under 1024px, which
    // is a desktop case, not a mobile one.
    //
    // The -5px rides inside the same `top` rather than arriving as a margin,
    // so the desktop placement stays one number to read. Fixed rather than
    // proportional — hence px subtracted from the percentage. This card keeps
    // its own small nudge: the 20px lift below is for the other two only.
    posSm: "sm:bottom-auto sm:top-[calc(24%_-_5px)]",
    from: "-56px",
    pop: "0.55s",
    bob: "0s",
    // From sm it slides in from the left and comes to rest with its right end
    // *under* the phone, so the phone reads as standing in front of it rather
    // than the card sitting flat on the glass. `z-[1]` is what buys that: the
    // phone is z-[2], and the laptop is z-[1] too but earlier in the DOM, so
    // the card still paints over the laptop — between the two devices,
    // exactly. Below sm it clears both devices, so the z has nothing to do.
    z: "z-[1]",
  },
  {
    icon: TrendingUp,
    iconClass: "bg-blue-100 text-blue-600",
    key: "repeat",
    // Short enough to keep the comp's placement at every width, so `pos`
    // carries it on its own; from sm it lifts 20px, which is a nudge on the
    // desktop composition rather than a second placement. 20px because the
    // cards bob 8px on their own (`jd-float`) — anything smaller is lost
    // inside that. The phone keeps 10% untouched.
    pos: "left-[50%] top-[10%]",
    posSm: "sm:top-[calc(10%_-_25px)] sm:left-[70%]",
    from: "-56px",
    pop: "1.2s",
    bob: "-1.2s",
  },
  {
    icon: ChefHat,
    iconClass: "bg-emerald-100 text-emerald-600",
    key: "kitchen",
    // Likewise: one placement, anchored to the right edge, plus the same 20px
    // desktop lift as the card above. Anchored on `bottom`, so up means a
    // *larger* value — the offset shrinks from -20% to -20% + 20px.
    pos: "right-[15%] bottom-[-20%]",
    posSm: "sm:bottom-[calc(-20%_+_20px)]",
    from: "56px",
    pop: "1.85s",
    bob: "-2.4s",
  },
];

/**
 * Hero carousel slide 1 — the comp's original composite shot: the product
 * running on laptop + phone + tablet, bleeding off the right edge.
 *
 * The laptop is the flow element that gives the wrapper its height; phone and
 * tablet hang off it in percentages of the wrapper width, so the arrangement
 * scales as one unit. The tablet's negative right offset recreates the comp's
 * bleed past the viewport edge; the section's overflow-hidden crops it.
 *
 * This is the slide the hero opens on and it holds the fold's largest image,
 * hence `priority` on all three devices — the other two slides load lazily.
 *
 * Entrances (devices via hero-piece-*, cards via the scoped jd-slide-in rule
 * in globals.css) all hang off the slide's `data-active`, so the whole scene
 * replays every time the reel comes back around — slide 1 renders with
 * data-active="true" in the SSR markup, so the first play still starts before
 * hydration.
 */
export default function DeviceCluster() {
  const { t } = useTranslation();

  return (
    <div className="relative w-full">
      <Image
        src="/v2/hero-laptop.webp"
        alt={t("home.mocks.alt.laptop")}
        width={2088}
        height={1309}
        priority
        quality={90}
        // 80vw, not the 90vw the className suggests: `w-[90%]` is 90% of the
        // *wrapper*, and the wrapper is itself ~90vw once the section padding
        // is out — measured at 298 CSS px on a 375px viewport, i.e. 79.5vw.
        // Same correction the phone below already carries; declaring the
        // className percentage as a viewport percentage over-asks by a full
        // candidate step. (Only the phone branch is retuned — the sm and lg
        // figures were not re-measured.)
        sizes="(max-width: 639px) 80vw, (max-width: 1023px) 78vw, 48vw"
        // Below sm the cluster is the phone comp's, not a shrunk copy of the
        // desktop one: 90% wide starting 12% in, rather than 78% pinned to the
        // right edge. Right-pinning is a desktop idea — there the stage is the
        // right-hand column of a two-column fold, so the machine leaning off
        // the outer edge reads as deliberate. Stacked under the copy on a
        // phone the same rule just parked the whole scene against the right
        // rule with a column of empty ground down the left.
        className="hero-piece-rise relative z-[1] ml-[12%] h-auto w-[90%] sm:ml-auto sm:w-[78%]"
      />
      <Image
        src="/v2/hero-phone.webp"
        alt={t("home.mocks.alt.phone")}
        width={646}
        height={1308}
        priority
        quality={90}
        // 19% of the *wrapper*, and the wrapper is ~90vw of a phone once the
        // section's 16px side paddings are out — so ~17vw of the viewport.
        // The 2vw mattered: at 412px/DPR 1.75 the 19vw figure asked for 137
        // device px, one past the 128w candidate, and got the 256w file.
        // The phone branch gets the same treatment the sm branch already had:
        // `w-[20%]` of a wrapper that is itself ~90vw works out to ~17.6vw
        // (measured 66 CSS px at 375), and 20vw asked for 144 device px at
        // 412/DPR 1.75 — one past the 128w candidate, so it was handed 256w.
        // 17vw lands on 128w. It under-asks by ~3%, which on a decorative
        // mockup at 66 px is not visible; the 20 KB is.
        sizes="(max-width: 639px) 17vw, (max-width: 1023px) 17vw, 12vw"
        // left-[7%] below sm: it keeps its place against the laptop, which now
        // starts 12% in rather than 22% (see above).
        className="hero-piece-left absolute bottom-[-10%] left-[7%] z-[2] h-auto w-[20%] sm:left-[20%] sm:w-[19%]"
        style={{ animationDelay: "0.16s" }}
      />
      <Image
        src="/v2/hero-tablet.webp"
        alt={t("home.mocks.alt.tablet")}
        width={1144}
        height={1587}
        priority
        quality={90}
        // 32vw for the same reason as the laptop above: measured 119 CSS px
        // on a 375px viewport, which is 31.7vw, not the declared 36.
        sizes="(max-width: 639px) 32vw, (max-width: 1023px) 34vw, 21vw"
        // The bleed shrinks from 10% to 5% below sm. It does not go to zero:
        // the comp still runs the tablet a little past the frame on a phone,
        // and a cluster whose three devices all stop dead on the same right
        // edge loses the depth the overlap is there for.
        className="hero-piece-right absolute bottom-[-15%] right-[-5%] z-[2] h-auto w-[36%] sm:right-[-10%] sm:w-[34%]"
        style={{ animationDelay: "0.26s" }}
      />

      {/* aria-hidden: these mimic app notifications but aren't real UI,
          so screen readers should skip them. */}
      {NOTIFY_CARDS.map((card) => (
        <div
          key={card.key}
          aria-hidden
          className={`jd-slide-in absolute ${card.z ?? "z-[3]"} ${card.pos} ${
            card.posSm ?? ""
          }`}
          style={
            {
              animationDelay: card.pop,
              "--jd-slide-from": card.from,
            } as React.CSSProperties
          }
        >
          <div
            className="jd-float flex items-center gap-2 rounded-xl border border-black/5 bg-white py-1.5 pl-2 pr-3 shadow-[0_10px_30px_rgba(15,23,42,0.14)] sm:gap-2.5 sm:rounded-2xl sm:py-2 sm:pl-2.5 sm:pr-4"
            style={{ animationDelay: card.bob }}
          >
            {card.img ? (
              <Image
                src={card.img}
                alt=""
                // Rendered at size-7 / sm:size-9 — 28-36 CSS px, never more.
                // width={96} asked Next for a 96w/256w pair and every 2x screen
                // took the 256, ~7x the pixels of the slot. 48 yields 48w/96w;
                // the 2x file still covers a 36 px slot with room over.
                width={48}
                height={48}
                className="size-7 shrink-0 rounded-lg object-cover sm:size-9"
              />
            ) : card.icon ? (
              <span
                className={`grid size-7 shrink-0 place-items-center rounded-full sm:size-9 ${card.iconClass}`}
              >
                <card.icon className="size-3.5 sm:size-4.5" aria-hidden />
              </span>
            ) : null}
            <span className="whitespace-nowrap">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-900 sm:text-[13px]">
                {t(`home.mocks.notify.${card.key}.title`)}
                {/* red-600, not red-500: at 8px this is the smallest text on
                    the page, and white on red-500 measures 3.8:1 — one step
                    down clears AA without reading as a different red. */}
                {card.live && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-px text-[8px] font-bold tracking-wide text-white sm:text-[9px]">
                    <span className="jd-live-blink size-1 rounded-full bg-white sm:size-1.5" />
                    LIVE
                  </span>
                )}
              </span>
              <span className="block text-[10px] text-gray-500 sm:text-[11px]">
                {t(`home.mocks.notify.${card.key}.sub`)}
              </span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
